import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { HoneybadgerErrorBoundary } from '@honeybadger-io/react';

import './lib/fonts';            // @fontsource imports — bundles WOFF2s
import './styles/globals.css';
import { loadConfig } from './lib/config';
import { Honeybadger, initHoneybadger } from './lib/honeybadger';
import { routeTree } from './routeTree.gen';
import { startWebVitals } from './lib/analytics/webVitals';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root element in index.html');
}

// Load runtime config (apiBaseUrl, Auth0, Honeybadger) before mounting React
// so any module reading getConfig() during render sees a populated value.
loadConfig().then(() => {
  // Configure Honeybadger from runtime config (no-op when no apiKey, e.g. local dev).
  const reporting = initHoneybadger();

  const app = (
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>
  );

  createRoot(rootEl).render(
    // Wrap in the Honeybadger boundary only when reporting is enabled, so a
    // React render error is captured; local dev (no key) is left untouched.
    reporting ? <HoneybadgerErrorBoundary honeybadger={Honeybadger}>{app}</HoneybadgerErrorBoundary> : app,
  );
  // T230 — Core Web Vitals reporting. Started after mount so it captures
  // real first-paint metrics, not the synchronous bootstrap.
  startWebVitals();
});
